/**
 * CommandPalette — Ctrl+K / Cmd+K.
 *
 * v2 : items génériques regroupés par catégorie. L'appelant fournit la
 * liste complète et la palette se charge du filtrage fuzzy, du tri par
 * score, et de la catégorisation visuelle. Catégories typiques côté shell :
 *   - "Onglets"  → switch entre Info / Prise en main / panels / Paramètres
 *   - "Hôtes"    → connect cross-session (envoie session:join + navigue)
 *   - "Récents"  → top 5 derniers items utilisés, persisté localStorage
 *
 * MVP v1 → v2 : la signature change. Le shell mappe maintenant un tableau
 * d'`CommandItem` (avec une action `onSelect`) au lieu d'un mapping section
 * implicite. Le `currentId` reste exposé pour le badge "courant".
 */
import { createSignal, createMemo, createEffect, For, Show, type Accessor, type Component } from "solid-js";
import "./command-palette.css";

export interface CommandItem {
  id: string;
  label: string;
  /** Hint optionnel (ex: "Panel · pktmon", "Hôte · Windows · en ligne"). */
  hint?: string;
  /** Catégorie pour le regroupement visuel (Onglets, Hôtes, Récents…). */
  category: string;
  /** Vrai si c'est l'item courant — affiche un badge "courant" et remonte
   *  en tête de la liste filtrée vide. */
  current?: boolean;
  /** Désactivé visuellement (ex: host hors ligne) — non sélectionnable. */
  disabled?: boolean;
  /** Action exécutée à la sélection (clic ou Enter). */
  onSelect: () => void;
}

export interface CommandPaletteProps {
  open: Accessor<boolean>;
  items: Accessor<CommandItem[]>;
  onClose: () => void;
  /** Callback appelé après onSelect, pour traquer les récents côté shell. */
  onUsed?: (item: CommandItem) => void;
}

// Match fuzzy : tous les caractères de q apparaissent dans `text` dans
// l'ordre. Renvoie un score (plus grand = match plus serré).
function fuzzyMatch(text: string, q: string): number | null {
  if (!q) return 0;
  const t = text.toLowerCase();
  const query = q.toLowerCase();
  let ti = 0;
  let score = 0;
  let consec = 0;
  for (const c of query) {
    const idx = t.indexOf(c, ti);
    if (idx < 0) return null;
    // Bonus si match consécutif (mots préfixe).
    if (idx === ti) consec += 1;
    else consec = 0;
    score += 10 - Math.min(idx - ti, 9) + consec * 2;
    ti = idx + 1;
  }
  // Bonus prefix complet.
  if (t.startsWith(query)) score += 50;
  return score;
}

export const CommandPalette: Component<CommandPaletteProps> = (props) => {
  const [query, setQuery] = createSignal("");
  const [activeIndex, setActiveIndex] = createSignal(0);
  let inputEl: HTMLInputElement | undefined;

  // Reset à chaque ouverture : query vide, focus, activeIndex = 0.
  createEffect(() => {
    if (props.open()) {
      setQuery("");
      setActiveIndex(0);
      queueMicrotask(() => inputEl?.focus());
    }
  });

  // Items filtrés et triés. Sans query : l'item courant remonte d'abord,
  // puis ordre naturel (l'appelant a déjà ordonné — Récents en haut, etc.).
  // Avec query : tri par score fuzzy, ex æquo conservés dans l'ordre.
  const visible = createMemo<CommandItem[]>(() => {
    const q = query().trim();
    const all = props.items();
    if (!q) {
      const sorted = [...all].sort((a, b) => {
        if (a.current && !b.current) return -1;
        if (b.current && !a.current) return 1;
        return 0;
      });
      return sorted;
    }
    const scored: Array<{ it: CommandItem; score: number }> = [];
    for (const it of all) {
      const score = fuzzyMatch(it.label + " " + (it.hint ?? ""), q);
      if (score != null) scored.push({ it, score });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.map((s) => s.it);
  });

  // Clamp l'index à la longueur courante (filtrage peut raccourcir).
  createEffect(() => {
    const n = visible().length;
    if (activeIndex() >= n) setActiveIndex(Math.max(0, n - 1));
  });

  function commit(item: CommandItem) {
    if (item.disabled) return;
    item.onSelect();
    props.onUsed?.(item);
    props.onClose();
  }

  // Saut au prochain index non-désactivé.
  function moveActive(delta: 1 | -1) {
    const list = visible();
    if (list.length === 0) return;
    let i = activeIndex();
    for (let step = 0; step < list.length; step++) {
      i = (i + delta + list.length) % list.length;
      if (!list[i].disabled) {
        setActiveIndex(i);
        return;
      }
    }
  }

  function onInputKeyDown(e: KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = visible()[activeIndex()];
      if (it) commit(it);
    } else if (e.key === "Escape") {
      e.preventDefault();
      props.onClose();
    }
  }

  // Regroupe la liste visible par catégorie pour les headers.
  const grouped = createMemo<Array<{ category: string; items: CommandItem[] }>>(() => {
    const list = visible();
    const out: Array<{ category: string; items: CommandItem[] }> = [];
    const map = new Map<string, CommandItem[]>();
    for (const it of list) {
      const cat = it.category;
      if (!map.has(cat)) {
        const arr: CommandItem[] = [];
        map.set(cat, arr);
        out.push({ category: cat, items: arr });
      }
      map.get(cat)!.push(it);
    }
    return out;
  });

  // Index absolu (toutes catégories) pour mettre en évidence activeIndex.
  function absoluteIndex(groupIdx: number, itemIdx: number): number {
    let total = 0;
    for (let g = 0; g < groupIdx; g++) total += grouped()[g].items.length;
    return total + itemIdx;
  }

  return (
    <Show when={props.open()}>
      <div class="cmd-palette__backdrop" onClick={() => props.onClose()} role="presentation">
        <div
          class="cmd-palette"
          role="dialog"
          aria-modal="true"
          aria-label="Palette de commandes"
          onClick={(e) => e.stopPropagation()}
        >
          <div class="cmd-palette__input-row">
            <span class="cmd-palette__icon" aria-hidden="true">
              ⌕
            </span>
            <input
              ref={(el) => (inputEl = el)}
              class="cmd-palette__input"
              type="text"
              placeholder="Onglets, hôtes, récents…"
              value={query()}
              onInput={(e) => setQuery(e.currentTarget.value)}
              onKeyDown={onInputKeyDown}
              aria-label="Recherche"
              autocomplete="off"
              autocorrect="off"
              spellcheck={false}
            />
            <kbd class="cmd-palette__kbd">Esc</kbd>
          </div>

          <ul class="cmd-palette__list" role="listbox">
            <Show
              when={visible().length > 0}
              fallback={<li class="cmd-palette__empty">Aucun résultat pour « {query()} »</li>}
            >
              <For each={grouped()}>
                {(group, gi) => (
                  <>
                    <li class="cmd-palette__group-label" aria-hidden="true">
                      {group.category}
                    </li>
                    <For each={group.items}>
                      {(it, i) => {
                        const abs = () => absoluteIndex(gi(), i());
                        return (
                          <li
                            role="option"
                            aria-selected={abs() === activeIndex()}
                            aria-disabled={it.disabled ? "true" : undefined}
                            class="cmd-palette__item"
                            classList={{
                              "cmd-palette__item--active": abs() === activeIndex(),
                              "cmd-palette__item--current": it.current === true,
                              "cmd-palette__item--disabled": it.disabled === true,
                            }}
                            onMouseEnter={() => !it.disabled && setActiveIndex(abs())}
                            onClick={() => commit(it)}
                          >
                            <span class="cmd-palette__label">{it.label}</span>
                            <Show when={it.hint}>
                              <span class="cmd-palette__hint">{it.hint}</span>
                            </Show>
                            <Show when={it.current}>
                              <span class="cmd-palette__badge">courant</span>
                            </Show>
                          </li>
                        );
                      }}
                    </For>
                  </>
                )}
              </For>
            </Show>
          </ul>

          <div class="cmd-palette__footer">
            <span>
              <kbd>↑</kbd> <kbd>↓</kbd> naviguer
            </span>
            <span>
              <kbd>↵</kbd> ouvrir
            </span>
            <span>
              <kbd>Esc</kbd> fermer
            </span>
          </div>
        </div>
      </div>
    </Show>
  );
};
